/**
 * LegalLayout — shared chrome for /privacy and /terms.
 *
 * Public, unauthenticated, dark-mode design system match. Includes
 * the WAVE wordmark, last-updated stamp, and reading-friendly width.
 * Built so platform reviewers (Meta, TikTok, YouTube) can land here
 * directly from a footer link and find what they need in <30s.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Waves } from 'lucide-react';

export default function LegalLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white">
      {/* Top nav — minimal, just brand + back-to-home */}
      <div className="border-b border-[#1F1F1F] bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-2 group">
            <Waves className="h-4 w-4 text-[#00D4FF] transition-transform group-hover:scale-110" />
            <span className="text-[14px] font-black tracking-tight">WAVE</span>
          </Link>
          <div className="flex items-center gap-5 text-[11px] uppercase tracking-[0.18em]">
            <Link to="/privacy" data-testid="legal-nav-privacy" className="text-gray-500 hover:text-white transition">Privacy</Link>
            <Link to="/terms" data-testid="legal-nav-terms" className="text-gray-500 hover:text-white transition">Terms</Link>
            <a href="mailto:legal@wavedsp.ai" className="text-gray-500 hover:text-white transition">Contact</a>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-14 lg:py-20" data-testid="legal-article">
        <header className="mb-10 space-y-2">
          <p className="text-[10px] uppercase tracking-[0.28em] font-black text-[#FF4D94]">Legal</p>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tight leading-[1.05]">{title}</h1>
          <p className="text-[12px] text-gray-500" data-testid="legal-last-updated">
            Last updated · {lastUpdated}
          </p>
        </header>

        {/* Long-form body — Tailwind prose-tuned for our dark theme */}
        <div className="space-y-7 text-[14.5px] leading-[1.75] text-gray-300 legal-prose">
          {children}
        </div>

        <footer className="mt-16 pt-8 border-t border-[#1F1F1F] text-[12px] text-gray-500 space-y-2">
          <p>
            Questions? Email{' '}
            <a href="mailto:legal@wavedsp.ai" className="text-[#00D4FF] hover:underline">legal@wavedsp.ai</a>.
          </p>
          <p className="text-gray-700">© {new Date().getFullYear()} WAVE DSP. All rights reserved.</p>
        </footer>
      </article>

      {/* Lightweight typography polish for legal-prose section.
          Keeps headings legible without pulling in @tailwindcss/typography. */}
      <style>{`
        .legal-prose h2 { color: #fff; font-size: 1.25rem; font-weight: 800; letter-spacing: -0.01em; margin-top: 2rem; margin-bottom: 0.6rem; }
        .legal-prose h3 { color: #fff; font-size: 1rem; font-weight: 800; margin-top: 1.4rem; margin-bottom: 0.4rem; }
        .legal-prose p { margin-bottom: 0.9rem; }
        .legal-prose ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 1rem; }
        .legal-prose ul li { margin-bottom: 0.4rem; }
        .legal-prose a { color: #00D4FF; }
        .legal-prose a:hover { text-decoration: underline; }
        .legal-prose strong { color: #fff; font-weight: 700; }
        .legal-prose code { background:#141414; border:1px solid #1f1f1f; padding:1px 6px; border-radius:4px; font-size:0.85em; color:#a78bfa; }
      `}</style>
    </div>
  );
}
